/**
 * BookingFlows Connect — My Flows.
 * Each flow is a shareable storefront of services with YOUR markup:
 * direct URL for texting/posting anywhere + iframe embed for websites.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { connectApi } from '../../utils/apiV2';
import { ConnectShell, money, CARD, CopyBtn } from '../../components/connect/ConnectShell';
import {
  Layers, Plus, Loader2, Trash2, ExternalLink, MapPin, ChevronDown,
  Link2, Code2, Pencil, Check, Store,
} from 'lucide-react';

const FlowCard = ({ flow, onChanged, delay }) => {
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(flow.name);
  const origin = window.location.origin;
  const directUrl = `${origin}/c/${flow.slug}`;
  const embedSnippet = `<iframe src="${directUrl}?embed=1" title="${flow.name}" style="width:100%;min-height:760px;border:0;border-radius:12px;" loading="lazy"></iframe>`;

  const rename = async () => {
    if (!name.trim() || name.trim() === flow.name) { setRenaming(false); return; }
    try {
      await connectApi.updateFlow(flow.id, { name: name.trim() });
      toast.success('Flow renamed');
      setRenaming(false);
      onChanged();
    } catch { toast.error('Rename failed'); }
  };

  const toggleActive = async () => {
    try {
      await connectApi.updateFlow(flow.id, { isActive: !flow.isActive });
      toast.success(flow.isActive ? 'Flow paused — its links stop working' : 'Flow is live again');
      onChanged();
    } catch { toast.error('Could not update'); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${flow.name}"? Its links and embeds will stop working.`)) return;
    try {
      await connectApi.deleteFlow(flow.id);
      toast.success('Flow deleted');
      onChanged();
    } catch { toast.error('Delete failed'); }
  };

  const updateMarkup = async (item, pct) => {
    const v = parseFloat(pct);
    if (Number.isNaN(v) || v < 0 || v > 100) return toast.error('Markup must be between 0 and 100%');
    if (v === item.markupPct) return;
    try {
      await connectApi.updateFlowItem(flow.id, item.id, { markupPct: v });
      toast.success('Markup updated');
      onChanged();
    } catch { toast.error('Update failed'); }
  };

  const removeItem = async (item) => {
    try {
      await connectApi.removeFlowItem(flow.id, item.id);
      toast.success('Removed from flow');
      onChanged();
    } catch { toast.error('Remove failed'); }
  };

  return (
    <div className={`${CARD} overflow-hidden cx-rise`} style={{ animationDelay: `${delay}ms` }} data-testid={`connect-flow-${flow.id}`}>
      <div className="px-5 py-4 flex items-center gap-3">
        <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${flow.isActive ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
          <Layers className="w-5 h-5" strokeWidth={1.75} />
        </span>
        <div className="min-w-0 flex-1">
          {renaming ? (
            <div className="flex items-center gap-2">
              <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && rename()} autoFocus data-testid={`connect-flow-rename-input-${flow.id}`} className="rounded-lg border border-slate-200 px-2 py-1 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500/30 w-48" />
              <button onClick={rename} className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center" data-testid={`connect-flow-rename-save-${flow.id}`}><Check className="w-3.5 h-3.5" /></button>
            </div>
          ) : (
            <p className="text-sm font-extrabold text-slate-900 truncate flex items-center gap-2">
              {flow.name}
              <button onClick={() => setRenaming(true)} className="text-slate-300 hover:text-slate-500" aria-label="Rename" data-testid={`connect-flow-rename-btn-${flow.id}`}><Pencil className="w-3 h-3" /></button>
            </p>
          )}
          <p className="text-[11px] text-slate-400">{flow.items.length} service{flow.items.length !== 1 ? 's' : ''} · {flow.isActive ? 'live' : 'paused'}</p>
        </div>
        <button
          type="button"
          onClick={toggleActive}
          data-testid={`connect-flow-active-toggle-${flow.id}`}
          className={`rounded-full px-3 py-1.5 text-[11px] font-extrabold transition-colors ${flow.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
        >
          {flow.isActive ? 'Live' : 'Paused'}
        </button>
        <button type="button" onClick={remove} aria-label="Delete flow" data-testid={`connect-flow-delete-${flow.id}`} className="w-8 h-8 rounded-full bg-slate-50 text-slate-400 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => setOpen(!open)} data-testid={`connect-flow-expand-${flow.id}`} className="w-8 h-8 rounded-full bg-slate-50 text-slate-500 flex items-center justify-center">
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-100">
          {/* Share row */}
          <div className="px-5 py-4 bg-emerald-50/40 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-0 bg-white rounded-xl border border-emerald-100 px-3 py-2">
                <Link2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span className="flex-1 text-[11px] font-mono text-slate-500 truncate">{directUrl}</span>
                <a href={directUrl} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-emerald-600" aria-label="Open flow" data-testid={`connect-flow-open-${flow.id}`}>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
              <CopyBtn text={directUrl} label="Copy link" testid={`connect-flow-copy-url-${flow.id}`} />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-0 bg-white rounded-xl border border-emerald-100 px-3 py-2">
                <Code2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span className="flex-1 text-[11px] font-mono text-slate-500 truncate">{embedSnippet}</span>
              </div>
              <CopyBtn text={embedSnippet} label="Copy embed" testid={`connect-flow-copy-embed-${flow.id}`} />
            </div>
            <p className="text-[11px] text-emerald-900/60">Text the link, post it, or embed it on any website — every booking through it earns your markup.</p>
          </div>

          {/* Items */}
          <div className="divide-y divide-slate-50">
            {flow.items.length === 0 && (
              <p className="px-5 py-6 text-center text-xs text-slate-400">Empty flow — add services from The Network.</p>
            )}
            {flow.items.map((it) => (
              <div key={it.id} className="px-5 py-3 flex items-center gap-3" data-testid={`connect-flow-item-${it.id}`}>
                <div className="w-11 h-11 rounded-xl bg-slate-100 overflow-hidden shrink-0">
                  {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-extrabold text-slate-800 truncate">{it.serviceName}</p>
                  <p className="text-[11px] text-slate-400 truncate flex items-center gap-1">
                    <MapPin className="w-3 h-3 shrink-0" />
                    {[it.city, it.country].filter(Boolean).join(', ') || '—'} · {it.operatorName}
                  </p>
                  <p className="text-[11px] text-slate-500">base {money(it.basePrice, it.currency)} → <span className="font-extrabold text-emerald-700">{money(it.yourPrice, it.currency)}</span></p>
                </div>
                <div className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1.5 shrink-0">
                  <span className="text-[11px] text-slate-400 font-bold">+</span>
                  <input
                    type="number" min="0" max="100" step="0.5" defaultValue={it.markupPct}
                    onBlur={(e) => updateMarkup(it, e.target.value)}
                    data-testid={`connect-flow-item-markup-${it.id}`}
                    className="w-11 text-xs font-extrabold text-slate-800 focus:outline-none"
                  />
                  <span className="text-[11px] text-slate-400 font-bold">%</span>
                </div>
                <button type="button" onClick={() => removeItem(it)} aria-label="Remove" data-testid={`connect-flow-item-remove-${it.id}`} className="w-8 h-8 rounded-full bg-slate-50 text-slate-400 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function ConnectFlows() {
  const navigate = useNavigate();
  const [flows, setFlows] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const load = useCallback(() => {
    connectApi.flows().then((r) => setFlows(r.data.flows || [])).catch(() => setFlows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newName.trim()) return toast.error('Give your flow a name');
    try {
      await connectApi.createFlow({ name: newName.trim() });
      toast.success('Flow created — now add services from The Network');
      setNewName('');
      setCreating(false);
      load();
    } catch { toast.error('Could not create flow'); }
  };

  return (
    <ConnectShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10" data-testid="connect-flows-page">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3 cx-rise">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-2 flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5" /> Your shareable storefronts
            </p>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">My Flows</h1>
            <p className="text-sm text-slate-500 mt-1.5">Bundle services, set your prices, share the link everywhere.</p>
          </div>
          <button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="connect-new-flow-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white text-sm font-extrabold px-5 py-2.5 hover:bg-emerald-700 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" /> New flow
          </button>
        </div>

        {creating && (
          <div className={`${CARD} p-4 mb-4 flex flex-col sm:flex-row gap-2 cx-rise`}>
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && create()}
              placeholder="Flow name — e.g. Austin Lake Day"
              data-testid="connect-new-flow-name-input"
              className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            />
            <div className="flex gap-2">
              <button type="button" onClick={create} data-testid="connect-new-flow-create-btn" className="rounded-full bg-slate-900 text-white text-xs font-extrabold px-5 py-2.5 hover:bg-emerald-700 transition-colors">Create</button>
              <button type="button" onClick={() => setCreating(false)} className="rounded-full bg-slate-100 text-slate-500 text-xs font-bold px-4 py-2.5">Cancel</button>
            </div>
          </div>
        )}

        {!flows && <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-emerald-300" /></div>}
        {flows && flows.length === 0 && !creating && (
          <div className={`${CARD} border-dashed p-12 text-center cx-rise`}>
            <Store className="w-9 h-9 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-600">No flows yet</p>
            <p className="text-xs text-slate-400 mt-1 mb-4">Create one, then add services from The Network — each flow gets its own link and embed.</p>
            <button type="button" onClick={() => navigate('/connect')} data-testid="connect-flows-browse-btn" className="rounded-full bg-emerald-600 text-white text-xs font-extrabold px-5 py-2.5 hover:bg-emerald-700 transition-colors">
              Browse The Network
            </button>
          </div>
        )}
        <div className="space-y-4">
          {(flows || []).map((f, i) => (
            <FlowCard key={f.id} flow={f} onChanged={load} delay={i * 70} />
          ))}
        </div>
      </div>
    </ConnectShell>
  );
}
